import {
  BedrockAgentCoreClient,
  InvokeAgentRuntimeCommand,
} from "@aws-sdk/client-bedrock-agentcore";
import {
  BedrockRuntimeClient,
  InvokeModelCommand,
} from "@aws-sdk/client-bedrock-runtime";
import { createAwsClient } from "./AwsAuth";
import {
  extractBetweenTags,
  removeCharFromStartAndEnd,
  handleFormatter,
} from "./Utils.js";
import {
  AWS_REGION,
  MODEL_ID_FOR_CHART,
  AGENT_RUNTIME_ARN,
  AGENT_ENDPOINT_NAME,
} from "../env.js";

// DynamoDB and chart generation removed for concrete sales assistant
// Chart functionality (MyChart component) kept for future use with weather/temperature data

/**
 * Invokes the agent core functionality with streaming support
 * @param {string} query - The user query to send to the agent
 * @param {string} sessionId - The session ID for the conversation
 * @param {string} queryUuid - The unique UUID for this query
 * @param {string} timezone - The user's timezone
 * @param {Function} setAnswers - State setter for answers (for streaming)
 * @param {Function} setControlAnswers - State setter for control answers (for streaming)
 * @param {number} lastKTurns - Number of previous conversation turns to maintain (default: 10)
 * @returns {Promise<Object>} - The agent's response with comprehensive data
 */
export const invokeAgentCore = async (
  query,
  sessionId = null,
  queryUuid = null,
  timezone = null,
  setAnswers,
  setControlAnswers,
  lastKTurns = 10,
  userEmail = null
) => {
  try {
    // Get bearer token from Cognito (use accessToken which has client_id claim)
    const { fetchAuthSession } = await import("aws-amplify/auth");
    const session = await fetchAuthSession();
    const bearerToken = session.tokens?.accessToken?.toString();
    
    if (!bearerToken) {
      throw new Error("Authentication token not available");
    }

    console.log("Invoking agent core with query:", query);
    
    // Decode JWT token to verify client_id
    try {
      const tokenParts = bearerToken.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log("JWT Token Payload:", {
          client_id: payload.client_id,
          aud: payload.aud,
          sub: payload.sub,
          token_use: payload.token_use,
          iss: payload.iss
        });
        console.log("Expected client_id: 3527o6i1nhsrj9ahtpp1ogas4t");
        if (payload.client_id !== '3527o6i1nhsrj9ahtpp1ogas4t') {
          console.error("❌ CLIENT_ID MISMATCH! Token has:", payload.client_id);
        } else {
          console.log("✅ Client ID matches expected value");
        }
      }
    } catch (e) {
      console.error("Failed to decode token:", e);
    }

    // Create the payload - use userEmail as actor_id for unique memory per user
    // Sanitize email to match actorId pattern: [a-zA-Z0-9][a-zA-Z0-9-/]*(?::[a-zA-Z0-9-/]+)[a-zA-Z0-9-_/]
    const actorId = userEmail ? userEmail.replace(/\+/g, '-').replace(/@/g, ':').replace(/\./g, '/') : "user";
    const payload = JSON.stringify({
      prompt: query,
      actor_id: actorId
    });

    // Use fetch with bearer token instead of AWS SDK
    const url = `https://bedrock-agentcore.${AWS_REGION}.amazonaws.com/runtimes/${encodeURIComponent(AGENT_RUNTIME_ARN)}/invocations?qualifier=${AGENT_ENDPOINT_NAME}`;
    
    console.log("Agent Core URL:", url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${bearerToken}`,
        'Content-Type': 'application/json',
        'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id': sessionId || "default-session"
      },
      body: payload
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`AgentCore request failed: ${response.status} - ${errorText}`);
    }

    let completion = "";
    let hasReceivedFirstChunk = false;

    // Initialize streaming output
    console.log("------- Agent Response (Streaming) -------");

    try {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        console.log("Chunk received:", chunk);
        
        chunk.split("\n").forEach((line) => {
          if (line.trim() && line.startsWith("data: ")) {
            let data = line.substring(6).trim();
            // Remove surrounding quotes if present
            data = data.replace(/^"|"$/g, '');
            // Convert escaped newlines to actual newlines
            data = data.replace(/\\n/g, '\n');
            if (data) {
              completion += data;
            }
          }
        });

        // Add initial answer object only after receiving the first chunk
        if (!hasReceivedFirstChunk) {
          hasReceivedFirstChunk = true;
          setAnswers((prevState) => [
            ...prevState,
            { text: completion, isStreaming: true },
          ]);
          setControlAnswers((prevState) => [
            ...prevState,
            { current_tab_view: "answer" },
          ]);
        } else {
          // Update the existing streaming answer with new text
          setAnswers((prevState) => {
            const newState = [...prevState];
            for (let i = newState.length - 1; i >= 0; i--) {
              if (newState[i].isStreaming) {
                newState[i] = {
                  ...newState[i],
                  text: completion,
                };
                break;
              }
            }
            return newState;
          });
        }
      }
    } catch (streamError) {
      console.error("Error processing agent response stream:", streamError);
      throw streamError;
    }

    console.log("------- End of Agent Response -------");
    console.log("Complete Streaming Output:", completion);
    return {
      sessionId,
      completion,
      lastKTurns,
    };
  } catch (error) {
    console.error("Error invoking agent core:", error);
    throw error;
  }
};
